import type {
  AIDecision,
  AIRequest,
  AIUsageBudget,
  AvailableLoad,
  BodyMeasurement,
  CardioLog,
  CardioPrescription,
  DailyActivity,
  EvidenceSearch,
  EvidenceSource,
  DailyWeight,
  Equipment,
  Gym,
  LoadRecommendationRow,
  MealLog,
  MealPlan,
  NutritionTarget,
  OutboxOp,
  PantryItem,
  PlannedMeal,
  PerformedSet,
  Profile,
  ProgressionStateRow,
  ReadinessCheckin,
  RestEvent,
  SessionExercise,
  SetPrescription,
  ShoppingList,
  ShoppingListItem,
  TrainingBlock,
  TrainingDecision,
  TrainingSession,
  TrainingWeek,
  UserPreferences,
  UserRecipeSetting,
  WarmupPrescription,
  WeeklyCheckin,
  WorkoutLog,
} from '@cadencia/contracts';
import Dexie, { type EntityTable } from 'dexie';
import { instalarNormalizacionAlLeer } from './normalizacion.js';
import { restaurarClavesQueSonDatos } from './sync/case.js';

/** Estado de sincronizacion; vive SOLO en local (docs/DATA_MODEL.md §15). */
export interface SyncState {
  id: 'singleton';
  lastPullSeq: number;
  /**
   * Cuando termino la ultima bajada, o `null` si no ha terminado ninguna.
   *
   * NO es lo mismo que `lastPullSeq > 0`: una cuenta sin nada en el servidor
   * baja cero filas y deja el cursor en cero, y eso es una bajada completada
   * igual. Distinguirlo importa porque este campo es lo unico que separa «no
   * hay fila» de «no la he mirado todavia» (NEW5-002).
   */
  lastPullAt: string | null;
  lastPushAt: string | null;
  deviceId: string;
  lastError: string | null;
}

export interface MetaRow {
  key: string;
  value: unknown;
}

export class CadenciaDb extends Dexie {
  profiles!: EntityTable<Profile, 'id'>;
  userPreferences!: EntityTable<UserPreferences, 'id'>;
  gyms!: EntityTable<Gym, 'id'>;
  equipment!: EntityTable<Equipment, 'id'>;
  availableLoads!: EntityTable<AvailableLoad, 'id'>;
  outbox!: EntityTable<OutboxOp, 'opId'>;
  syncState!: EntityTable<SyncState, 'id'>;
  meta!: EntityTable<MetaRow, 'key'>;

  // Fase 2 — programacion y ejecucion
  trainingBlocks!: EntityTable<TrainingBlock, 'id'>;
  trainingWeeks!: EntityTable<TrainingWeek, 'id'>;
  trainingSessions!: EntityTable<TrainingSession, 'id'>;
  sessionExercises!: EntityTable<SessionExercise, 'id'>;
  setPrescriptions!: EntityTable<SetPrescription, 'id'>;
  warmupPrescriptions!: EntityTable<WarmupPrescription, 'id'>;
  readinessCheckins!: EntityTable<ReadinessCheckin, 'id'>;
  workoutLogs!: EntityTable<WorkoutLog, 'id'>;
  performedSets!: EntityTable<PerformedSet, 'id'>;
  restEvents!: EntityTable<RestEvent, 'id'>;
  loadRecommendations!: EntityTable<LoadRecommendationRow, 'id'>;
  progressionState!: EntityTable<ProgressionStateRow, 'id'>;

  // Fase 3 — motor de programacion
  trainingDecisions!: EntityTable<TrainingDecision, 'id'>;
  cardioPrescriptions!: EntityTable<CardioPrescription, 'id'>;
  cardioLogs!: EntityTable<CardioLog, 'id'>;

  // Fase 4 — nutricion
  nutritionTargets!: EntityTable<NutritionTarget, 'id'>;
  mealPlans!: EntityTable<MealPlan, 'id'>;
  plannedMeals!: EntityTable<PlannedMeal, 'id'>;
  mealLogs!: EntityTable<MealLog, 'id'>;
  dailyWeights!: EntityTable<DailyWeight, 'id'>;
  userRecipeSettings!: EntityTable<UserRecipeSetting, 'id'>;

  // Fase 5 — compra y despensa
  pantryItems!: EntityTable<PantryItem, 'id'>;
  shoppingLists!: EntityTable<ShoppingList, 'id'>;
  shoppingListItems!: EntityTable<ShoppingListItem, 'id'>;

  // Fase 6 — progreso
  bodyMeasurements!: EntityTable<BodyMeasurement, 'id'>;
  weeklyCheckins!: EntityTable<WeeklyCheckin, 'id'>;
  dailyActivity!: EntityTable<DailyActivity, 'id'>;

  // Evidencia cientifica (§8, migracion 0031).
  evidenceSearches!: EntityTable<EvidenceSearch, 'id'>;
  evidenceSources!: EntityTable<EvidenceSource, 'id'>;

  // Fase 7 — IA. Las tres bajan por sincronizacion; `aiRequests` y
  // `aiUsageBudget` solo bajan: las escribe el servidor (migracion 0020).
  aiRequests!: EntityTable<AIRequest, 'id'>;
  aiDecisions!: EntityTable<AIDecision, 'id'>;
  aiUsageBudget!: EntityTable<AIUsageBudget, 'id'>;

  constructor(name = 'cadencia') {
    super(name);
    // v1: entidades de Fase 1. Cada version declara su migracion y no se
    // modifica una vez publicada (docs/SYNC_ENGINE.md §9).
    this.version(1).stores({
      profiles: 'id, userId, updatedAt, syncSeq',
      userPreferences: 'id, userId, updatedAt, syncSeq',
      gyms: 'id, userId, updatedAt, syncSeq',
      equipment: 'id, userId, gymId, updatedAt, syncSeq',
      availableLoads: 'id, userId, equipmentId, loadKg, syncSeq',
      outbox: 'opId, status, createdAt, entity',
      syncState: 'id',
      meta: 'key',
    });

    // v2: entidades de Fase 2. Solo añade tablas nuevas, asi que no necesita
    // `upgrade()`: los datos de la v1 se conservan intactos.
    this.version(2).stores({
      trainingBlocks: 'id, userId, status, startedOn, syncSeq',
      trainingWeeks: 'id, userId, blockId, weekIndex, syncSeq',
      trainingSessions: 'id, userId, weekId, blockId, scheduledDate, status, syncSeq',
      sessionExercises: 'id, userId, sessionId, orderIndex, syncSeq',
      setPrescriptions: 'id, userId, sessionExerciseId, setIndex, syncSeq',
      warmupPrescriptions: 'id, userId, sessionExerciseId, orderIndex, syncSeq',
      readinessCheckins: 'id, userId, forDate, syncSeq',
      workoutLogs: 'id, userId, sessionId, startedAt, status, syncSeq',
      performedSets: 'id, userId, workoutLogId, exerciseSlug, performedAt, syncSeq',
      restEvents: 'id, userId, workoutLogId, syncSeq',
      loadRecommendations: 'id, userId, workoutLogId, syncSeq',
      progressionState: 'id, userId, exerciseSlug, syncSeq',
    });

    // v3: registro de decisiones. Solo añade una tabla, asi que tampoco
    // necesita `upgrade()`: lo de la v2 se conserva intacto.
    this.version(3).stores({
      trainingDecisions: 'id, userId, decisionType, approvalStatus, decidedAt, syncSeq',
    });

    // v4: cardio. Solo añade tablas, asi que tampoco necesita `upgrade()` y los
    // datos existentes se conservan intactos.
    this.version(4).stores({
      cardioPrescriptions: 'id, userId, scheduledDate, sessionId, syncSeq',
      cardioLogs: 'id, userId, performedOn, prescriptionId, syncSeq',
    });

    // v5: nutricion. Solo anade tablas, asi que tampoco necesita `upgrade()`.
    this.version(5).stores({
      nutritionTargets: 'id, userId, targetDate, syncSeq',
      mealPlans: 'id, userId, weekStart, status, syncSeq',
      plannedMeals: 'id, userId, mealPlanId, mealDate, slot, status, syncSeq',
      mealLogs: 'id, userId, mealDate, slot, syncSeq',
      dailyWeights: 'id, userId, measuredOn, syncSeq',
      userRecipeSettings: 'id, userId, recipeSlug, syncSeq',
    });

    // v6: compra y despensa. Solo anade tablas, asi que tampoco necesita
    // `upgrade()` y lo de la v5 se conserva intacto.
    this.version(6).stores({
      pantryItems: 'id, userId, foodSlug, syncSeq',
      shoppingLists: 'id, userId, rangeStart, status, syncSeq',
      shoppingListItems: 'id, userId, shoppingListId, foodSlug, section, syncSeq',
    });

    // v7: progreso. Solo anade tablas, asi que tampoco necesita `upgrade()`.
    this.version(7).stores({
      bodyMeasurements: 'id, userId, measuredOn, syncSeq',
      weeklyCheckins: 'id, userId, weekStart, syncSeq',
    });

    // v8: IA. Solo anade tablas, asi que tampoco necesita `upgrade()`.
    //
    // Las tres son obligatorias aunque el cliente solo escriba una: la
    // migracion 0020 dio de alta las tres en `synced_entities` y `types.ts` ya
    // las mapea a estos nombres. Sin declararlas aqui, el primer `sync_pull`
    // que devolviera una fila de IA llamaria a `db.table('aiRequests')` y
    // lanzaria, rompiendo la sincronizacion ENTERA y no solo la parte de IA.
    this.version(8).stores({
      aiRequests: 'id, userId, purpose, status, contextHash, createdAt, syncSeq',
      aiDecisions: 'id, userId, aiRequestId, decisionId, syncSeq',
      aiUsageBudget: 'id, userId, periodMonth, syncSeq',
    });

    // v9: pasos diarios (§17, migracion 0028). Solo anade una tabla.
    //
    // Obligatoria por el mismo motivo que las de la v8: en cuanto el servidor
    // tenga una fila de `daily_activity`, el primer `sync_pull` que la traiga
    // llamaria a `db.table('dailyActivity')`.
    this.version(9).stores({
      dailyActivity: 'id, userId, activityDate, syncSeq',
    });

    /*
     * v10: la base de evidencia (§8, migracion 0031). Solo anade dos tablas.
     *
     * Obligatoria por el mismo motivo que la v9: en cuanto el servidor tenga
     * una fila, el primer `sync_pull` que la traiga llamaria a
     * `db.table('evidenceSources')` y no existiria.
     *
     * `status` va indexado porque la pregunta que se hace todo el rato es «que
     * fichas puedo usar», y esa filtra por ahi. `claims` NO se indexa: es un
     * array y Dexie no indexa arrays sin `multiEntry`, que aqui no compensa
     * —son decenas de filas, no miles— y ademas la trampa de CLAUDE.md avisa de
     * que un `where` sobre una columna no indexada revienta en ejecucion.
     */
    this.version(10).stores({
      evidenceSearches: 'id, userId, searchedOn, syncSeq',
      evidenceSources: 'id, userId, status, syncSeq',
    });

    /*
     * v11: la primera con `upgrade()`, y es una reparacion, no un esquema.
     *
     * Hasta el 23/09/2026 la bajada convertia a camelCase tambien las claves de
     * `volumeTargets` y `musclePriorities`, que son MUSCULOS: un bloque bajado
     * de cero guardaba `deltoidePosterior`, dejaba de encajar con su contrato
     * y la semana no se podia preparar. Arreglar la conversion no basta,
     * porque esa fila no se vuelve a escribir mientras el servidor no la
     * cambie. Esto la deja como esta en el servidor (`case.ts`).
     */
    this.version(11)
      .stores({})
      .upgrade(async (tx) => {
        await tx
          .table('trainingBlocks')
          .toCollection()
          .modify((fila: Record<string, unknown>, ref: { value: unknown }) => {
            const reparada = restaurarClavesQueSonDatos(fila);
            if (reparada !== null) ref.value = reparada;
          });
      });
  }
}

export const db = new CadenciaDb();

// Toda lectura pasa por su contrato antes de llegar al codigo que la usa
// (deuda D-4 / riesgo R-16). Ver `normalizacion.ts`.
instalarNormalizacionAlLeer(db);

export async function getSyncState(database: CadenciaDb = db): Promise<SyncState | undefined> {
  return database.syncState.get('singleton');
}

export async function initSyncState(
  deviceId: string,
  database: CadenciaDb = db,
): Promise<SyncState> {
  const existing = await database.syncState.get('singleton');
  if (existing) return existing;
  const fresh: SyncState = {
    id: 'singleton',
    lastPullSeq: 0,
    lastPullAt: null,
    lastPushAt: null,
    deviceId,
    lastError: null,
  };
  await database.syncState.put(fresh);
  return fresh;
}

/**
 * Destruye la base local. Se usa al detectar que pertenece a otro usuario o al
 * eliminar la cuenta (docs/SYNC_ENGINE.md §8 y docs/SECURITY_PRIVACY.md §8.3).
 */
export async function destroyLocalDatabase(database: CadenciaDb = db): Promise<void> {
  await database.delete();
}
